import { useMemo, useCallback, useRef, useState } from "react";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import { DataGrid, usePendingCellValue } from "@platform/design-system";

import { ColumnsPanel } from "../sidebar/ColumnsPanel";
import { CoveragesPanel } from "../sidebar/CoveragesPanel";

import { parseColId } from "./buildPatchDiff";
import { resolveCellChangeDiff } from "./handleCellValueChanged";
import {
  buildBlockById,
  buildCoverageIdsByBlock,
  buildCoverageIdToBlockName,
  buildOrphanedCoverageIds,
} from "./buildContextMaps";
import { buildFinancialsColDefs } from "./buildColDefs";
import { buildFinancialsRowData } from "./buildRowData";
import { FullWidthRowRenderer } from "./cells/FullWidthRowRenderer";
import { SINGLE_LINE_ROW_HEIGHT } from "./constants";
import { getFinancialsRowStyle } from "./rowStyles";
import { getFinancialsRowHeight } from "./rowHeight";
import { financialsRowKinds } from "./types";

import type { ColumnContext } from "./valueGetters";
import type { MpxAgGridStateProps } from "@platform/hooks";
import type { FinancialsGridContext, FinancialsRow } from "./types";
import type {
  CarrierColumn,
  FinancialsTableResponse,
  PatchFinancialsTableRequest,
} from "@platform/api";
import type {
  CellValueChangedEvent,
  GetRowIdParams,
  IsFullWidthRowParams,
  RowHeightParams,
  SideBarDef,
} from "ag-grid-community";

interface FinancialsGridProps {
  payload: FinancialsTableResponse;
  queueChange: (diff: PatchFinancialsTableRequest) => void;
  toggleCarrierHidden: (
    carrierId: string,
    currentlyHidden: boolean,
    carriers: readonly CarrierColumn[],
  ) => void;
  createTotalsBlock: (
    label: string,
    coverageIds: string[],
    position: number,
  ) => void;
  deleteTotalsBlock: (blockId: string) => void;
  revertPremium: (coverageId: string, column: ColumnContext) => void;
  premiumOverridesEnabled: boolean;
  gridProps: MpxAgGridStateProps;
}

const getRowId = (params: GetRowIdParams<FinancialsRow>): string =>
  params.data.rowId;

const FULL_WIDTH_ROW_KINDS: ReadonlySet<string> = new Set([
  financialsRowKinds.COVERAGE_HEADER,
  financialsRowKinds.TOTALS_HEADER,
]);

const isFullWidthRow = (params: IsFullWidthRowParams<FinancialsRow>): boolean =>
  params.rowNode.data !== undefined &&
  FULL_WIDTH_ROW_KINDS.has(params.rowNode.data.kind);

export const FinancialsGrid = ({
  payload,
  queueChange,
  toggleCarrierHidden,
  createTotalsBlock,
  deleteTotalsBlock,
  revertPremium,
  premiumOverridesEnabled,
  gridProps,
}: FinancialsGridProps): React.JSX.Element => {
  const [collapsedCoverages, setCollapsedCoverages] = useState<Set<string>>(
    () => new Set(),
  );

  const { rows, bottomRows, coverageByRowId } = useMemo(
    () => buildFinancialsRowData(payload),
    [payload],
  );

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.kind === financialsRowKinds.COVERAGE_HEADER ||
          !r.coverageId ||
          !collapsedCoverages.has(r.coverageId),
      ),
    [rows, collapsedCoverages],
  );

  const rowData = useMemo(
    () => [...visibleRows, ...bottomRows],
    [visibleRows, bottomRows],
  );

  const toggleCoverageExpanded = useCallback((coverageId: string) => {
    setCollapsedCoverages((prev) => {
      const next = new Set(prev);
      if (next.has(coverageId)) {
        next.delete(coverageId);
      } else {
        next.add(coverageId);
      }
      return next;
    });
  }, []);

  const getRowHeight = useCallback(
    (params: RowHeightParams<FinancialsRow>): number | undefined =>
      getFinancialsRowHeight(params.data?.kind),
    [],
  );

  const { pendingRef, setPendingCellValue } = usePendingCellValue(payload);

  const columnDefs = useMemo(() => buildFinancialsColDefs(payload), [payload]);

  const contextRef = useRef<FinancialsGridContext>(null!);
  contextRef.current = useMemo(
    () => ({
      payload,
      pendingRef,
      coverageByRowId,
      coverageById: new Map(payload.coverages.map((c) => [c.id, c])),
      carrierById: new Map(payload.carriers.map((c) => [c.id, c])),
      coverageIdsByBlock: buildCoverageIdsByBlock(payload),
      blockById: buildBlockById(payload.totalsBlocks),
      coverageIdToBlockName: buildCoverageIdToBlockName(payload.totalsBlocks),
      orphanedCoverageIds: buildOrphanedCoverageIds(payload),
      queueChange,
      toggleCarrierHidden,
      createTotalsBlock,
      deleteTotalsBlock,
      revertPremium,
      premiumOverridesEnabled,
      collapsedCoverages,
      toggleCoverageExpanded,
    }),
    [
      payload,
      pendingRef,
      coverageByRowId,
      queueChange,
      toggleCarrierHidden,
      createTotalsBlock,
      deleteTotalsBlock,
      revertPremium,
      premiumOverridesEnabled,
      collapsedCoverages,
      toggleCoverageExpanded,
    ],
  );

  const sideBar: SideBarDef = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: ColumnsPanel,
        },
        {
          id: "coverages",
          labelDefault: "Coverages",
          labelKey: "coverages",
          iconKey: "filter",
          toolPanel: CoveragesPanel,
        },
      ],
    }),
    [],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<FinancialsRow>) => {
      const { colId } = event.colDef;

      if (colId !== undefined && event.data !== undefined) {
        setPendingCellValue(event.data.rowId, colId, event.newValue);
      }

      // Clearing a premium removes the analyst's override, which only the
      // server can replace with the calculated value: save it and refetch,
      // the same as the revert button.
      const column = colId === undefined ? null : parseColId(colId);
      const coverageId = event.data
        ? coverageByRowId.get(event.data.rowId)
        : undefined;
      const clearsPremium =
        event.data?.kind === financialsRowKinds.ANNUAL_PREMIUM &&
        (event.newValue === "" || event.newValue == null);
      if (clearsPremium && column && coverageId !== undefined) {
        revertPremium(coverageId, column);
        return;
      }

      const diff = resolveCellChangeDiff({
        data: event.data,
        colId,
        newValue: event.newValue,
        coverageByRowId,
        totalsBlocks: contextRef.current.payload.totalsBlocks,
      });

      if (diff) queueChange(diff);
    },
    [queueChange, revertPremium, coverageByRowId, setPendingCellValue],
  );

  const { key: gridKey, ...restGridProps } = gridProps;

  return (
    <DataGrid<FinancialsRow>
      key={gridKey}
      {...restGridProps}
      containerProps={{ "data-testid": "financials-grid" }}
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        "& .ag-cell": {
          display: "flex",
          alignItems: "center",
        },
      }}
      modules={[AllEnterpriseModule]}
      rowData={rowData}
      columnDefs={columnDefs}
      context={contextRef.current}
      getRowId={getRowId}
      getRowStyle={getFinancialsRowStyle}
      isFullWidthRow={isFullWidthRow}
      fullWidthCellRenderer={FullWidthRowRenderer}
      headerHeight={SINGLE_LINE_ROW_HEIGHT}
      getRowHeight={getRowHeight}
      stopEditingWhenCellsLoseFocus
      sideBar={sideBar}
      onCellValueChanged={handleCellValueChanged}
      defaultColDef={{
        flex: 0,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        sortable: false,
        resizable: false,
      }}
      themeParams={{
        headerColumnBorder: true,
      }}
    />
  );
};
